import { useIsMutating } from '@tanstack/react-query'
import { Check, Circle } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { parseError } from '@/lib/errors'
import { formatDate } from '@/lib/formatters'
import {
  ENDORSEMENT_STEP_KEY,
  EndorsementStaffAction,
  endorsementSteps,
  type InternalEndorsement,
  useEndorsementStep,
} from '@/models/internalEndorsement'

/** After the customer accepts: bind with each carrier, see the policies change, then issue the COI. */
export function EndorsementBind({ endorsement }: { endorsement: InternalEndorsement }) {
  const bound = useEndorsementStep(endorsementSteps.markBound)
  const updated = useEndorsementStep(endorsementSteps.markPolicyUpdated)
  const complete = useEndorsementStep(endorsementSteps.complete)
  const stepping = useIsMutating({ mutationKey: ENDORSEMENT_STEP_KEY }) > 0
  const steps = [
    {
      label: 'Bound with every carrier',
      doneAt: endorsement.boundAt,
      action: EndorsementStaffAction.MARK_BOUND,
      button: 'Mark bound',
      mutation: bound,
      success: 'Marked bound. Changes dated today or earlier are on the roster.',
    },
    {
      label: 'Policies show the change',
      doneAt: endorsement.policyUpdatedAt,
      action: EndorsementStaffAction.MARK_POLICY_UPDATED,
      button: 'Mark policies updated',
      mutation: updated,
      success: 'Marked the policies updated.',
    },
    {
      label: 'New COI issued',
      doneAt: endorsement.completedAt,
      action: EndorsementStaffAction.COMPLETE,
      button: 'Issue updated COI',
      mutation: complete,
      success: 'The customer’s COIs are being reissued.',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bind and COI</CardTitle>
        <p className="text-sm text-neutral-600">
          Once the customer accepts. A change dated later goes on the roster on its day.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {steps.map((step) => (
          <div key={step.label} className="flex min-h-9 items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2">
              {step.doneAt ? (
                <Check className="size-4 text-green-600" />
              ) : (
                <Circle className="size-4 text-neutral-300" />
              )}
              {step.label}
            </span>
            {step.doneAt ? (
              <span className="text-neutral-500">{formatDate(step.doneAt)}</span>
            ) : (
              endorsement.actions.includes(step.action) && (
                <Button
                  type="button"
                  size="sm"
                  disabled={stepping}
                  loading={step.mutation.isPending ? 'Saving…' : undefined}
                  onClick={() =>
                    step.mutation.mutate(endorsement.id, {
                      onSuccess: () => toast.success(step.success),
                      onError: (error) => toast.error(parseError(error)),
                    })
                  }
                >
                  {step.button}
                </Button>
              )
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
